import { Group } from 'src/design/atoms/Group';
import { Text } from 'src/design/atoms/Text';
import { Tabs } from 'src/design/molecules/Tabs';
import { useFormik } from 'formik';
import React from 'react';
import {
  JobClassificationRateRequestDto,
  JobPayCodeRequestDto,
  JobSummaryDto,
} from 'src/api/FiretrolApiClient';
import { useGetOrCreateCtcDivision } from 'src/api/queries/ctc/useCtcDivisionMutations';
import { useCreateJob } from 'src/api/queries/jobs/useJobMutations';
import { IDivision } from 'src/api/models/Division';
import { useDialogs } from 'src/hooks/useDialogs';
import { SaveButton } from 'src/design/atoms/SaveButton';
import { Modal } from 'src/design/molecules/Modal';
import * as Yup from 'yup';
import { getErrorMessage } from 'src/utils';
import { BasicInfoTab } from './BasicInfoTab';
import { ClassificationRatesOverride } from './ClassificationRatesOverride';
import { PayCodeOverrides } from './PayCodeOverrides';

export interface ICreateJobDialogProps {
  onCreateComplete?: () => void;
}

export interface ILocation {
  address: string;
  geo: { lat: number; lng: number } | null;
}

export interface ICreateJobDialogFormValues {
  name: string;
  jobNumber: string;
  description: string;
  taxExempt: boolean;
  vendorDiscounts: boolean;
  division: IDivision | null;
  customerNumber: string | null;
  customerName: string | null;
  location: ILocation;
  documentTemplateId: string | null;
  changeTemplateId: string | null;
  milestoneTemplateId: string | null;
  perDiemEnabled: boolean;
  perDiemAmount: number | null;
  scalePayEnabled: boolean;
  classificationRates: Omit<JobClassificationRateRequestDto, 'jobId'>[];
  payCodeOverrides: Omit<JobPayCodeRequestDto, 'jobId'>[];
}

const defaultLocation: ILocation = {
  address: '',
  geo: null,
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required().min(3, 'Name must be at least 3 characters'),
  jobNumber: Yup.string().required('Job Number is required'),
  description: Yup.string().required('Scope of Work is required'),
  customerNumber: Yup.string().nullable().required('Customer is required'),
  customerName: Yup.string().nullable().required('Customer is required'),
  division: Yup.object().nullable().required('Division is required'),
  location: Yup.object().nullable().required('Jobsite Location is required'),
  perDiemEnabled: Yup.boolean(),
  perDiemAmount: Yup.number()
    .nullable()
    .when('perDiemEnabled', {
      is: true,
      then: (schema) =>
        schema
          .required('Per Diem Amount is required')
          .min(0, 'Per Diem Amount must be positive'),
    }),
  scalePayEnabled: Yup.boolean(),
  classificationRates: Yup.array().when('scalePayEnabled', {
    is: true,
    then: (schema) =>
      schema
        .min(
          1,
          'At least one classification rate is required when scale pay is enabled',
        )
        .test(
          'all-rates-complete',
          'All classifications must have both scale rate and fringe rate',
          (value) => {
            if (!value || value.length === 0) return false;
            return value.every(
              (rate: any) =>
                rate.scaleRate !== undefined &&
                rate.scaleRate !== null &&
                rate.scaleRate > 0 &&
                rate.fringeRate !== undefined &&
                rate.fringeRate !== null &&
                rate.fringeRate > 0,
            );
          },
        ),
  }),
});

/** @registry Full-screen modal for creating a job, with basic info, pay code override, and scale-rate tabs. */
export const CreateJobDialog: React.FC<ICreateJobDialogProps> = ({
  onCreateComplete,
}) => {
  const { createProjectOpen, setCreateProjectOpen, ctcJob } = useDialogs();
  const createJob = useCreateJob();
  const getOrCreateDivision = useGetOrCreateCtcDivision();
  const [error, setError] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState<string | null>('basic');

  const formik = useFormik<ICreateJobDialogFormValues>({
    initialValues: {
      name: '',
      jobNumber: '',
      description: '',
      taxExempt: false,
      vendorDiscounts: false,
      division: null,
      customerNumber: null,
      customerName: null,
      location: defaultLocation,
      documentTemplateId: null,
      changeTemplateId: null,
      milestoneTemplateId: null,
      perDiemEnabled: false,
      perDiemAmount: null,
      scalePayEnabled: false,
      classificationRates: [],
      payCodeOverrides: [],
    },
    validationSchema,
    validateOnMount: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      try {
        setError(null);
        await createJob.mutateAsync({
          name: values.name,
          divisionId: values.division?.id,
          description: values.description,
          jobNumber: values.jobNumber,
          customerName: values.customerName || undefined,
          customerNumber: values.customerNumber || undefined,
          location: values.location.address,
          documentTemplateId: values.documentTemplateId || undefined,
          milestoneTemplateId: values.milestoneTemplateId || undefined,
          changeTemplateId: values.changeTemplateId || undefined,
          vendorDiscounts: values.vendorDiscounts,
          taxExempt: values.taxExempt,
          perDiemEnabled: values.perDiemEnabled,
          perDiemAmount: values.perDiemAmount || undefined,
          scalePayEnabled: values.scalePayEnabled,
          classificationRates: values.classificationRates.map((rate) => ({
            classificationId: rate.classificationId,
            scaleRate: rate.scaleRate,
            fringeRate: rate.fringeRate,
          })),
          payCodeOverrides: values.payCodeOverrides.map((override) => ({
            payCodeId: override.payCodeId,
            rateModifierMultiplier: override.rateModifierMultiplier,
          })),
        });
        closeDialog();
      } catch (err) {
        setError(getErrorMessage(err));
      }
    },
  });

  const { values, setFieldValue, setFieldTouched, errors, touched, isValid } =
    formik;

  const closeDialog = () => {
    setCreateProjectOpen(false);
    formik.resetForm();
    setError(null);
    setActiveTab('basic');
    onCreateComplete?.();
  };

  React.useEffect(() => {
    onCTCJobChange(ctcJob);
  }, [ctcJob]);

  const onCTCJobChange = (job: JobSummaryDto | null) => {
    if (job) {
      setFieldValue('name', job.wsJobName);
      setFieldValue('jobNumber', job.wsJobNumber);
      setFieldValue('description', job.wsJobName);
      setFieldValue('customerNumber', job.custNmbr);
      setFieldValue('customerName', job.custName);

      setFieldTouched('description', true);

      getOrCreateDivision.mutate(job.divisions, {
        onSuccess: (division) => {
          setFieldValue('division', division);
          setFieldTouched('division', true);
        },
      });
    } else {
      setFieldValue('name', '');
      setFieldValue('jobNumber', '');
      setFieldValue('description', '');
      setFieldValue('customerNumber', null);
      setFieldValue('customerName', null);
      setFieldValue('division', null);
    }
  };

  return (
    <Modal
      opened={createProjectOpen}
      onClose={closeDialog}
      title={`Create Job${values.name ? ` - ${values.name}` : ''}`}
      fullScreen={true}
      actions={[
        <SaveButton
          key="save"
          onClick={() => formik.handleSubmit()}
          loading={createJob.isPending}
          disabled={!isValid || createJob.isPending}
        />,
      ]}
    >
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="basic">Basic Info</Tabs.Tab>
          <Tabs.Tab value="paycodes">Pay Code Overrides</Tabs.Tab>
          <Tabs.Tab value="classifications" disabled={!values.scalePayEnabled}>
            <Group gap="xs">
              <Text size="sm" c="bright">
                Scale Pay Rates
              </Text>
              {values.scalePayEnabled && (
                <Text size="xs" c="red">
                  *
                </Text>
              )}
            </Group>
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="basic" pt="md">
          <BasicInfoTab
            values={values}
            touched={touched}
            errors={errors}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            onCTCJobChange={onCTCJobChange}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            saveError={error}
          />
        </Tabs.Panel>

        <Tabs.Panel value="paycodes" pt="md">
          <PayCodeOverrides
            value={values.payCodeOverrides}
            onChange={(overrides) =>
              setFieldValue('payCodeOverrides', overrides)
            }
          />
        </Tabs.Panel>

        <Tabs.Panel value="classifications" pt="md">
          <ClassificationRatesOverride
            value={values.classificationRates}
            onChange={(rates) => setFieldValue('classificationRates', rates)}
          />
          {touched.classificationRates && errors.classificationRates && (
            <Text c="red" size="sm" mt="md">
              {typeof errors.classificationRates === 'string'
                ? errors.classificationRates
                : 'Please ensure all classification rates are complete'}
            </Text>
          )}
        </Tabs.Panel>
      </Tabs>
    </Modal>
  );
};
